import { Card, CardFooter, CardHeader } from "@fanduel/og-fe-library";
import { useState, useMemo } from "react";
import Divider from "@/components/divider";
import NavigationLink from "@/components/navigationLink";
import {
  SPORTSBOOK_URL,
  SPORTSBOOK_ODDS_CTA_CLICKED,
  SPORTSBOOK_ODDS_SELECTED,
  SPORTSBOOK_BETSLIP_CTA_CLICKED,
  SPORTSBOOK_READY_TO_BET_MODAL_CLOSED,
} from "@/constants/amplitude";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useArticleInfo } from "@/utils/hooks/useArticleContext";
import { filterEmpty } from "@/utils/index";
import { DynamicOdds } from "../tempTypes";
import Selection from "./Selection";
import Styles from "./FuturesProps.module.scss";

interface FuturesPropsProps extends DynamicOdds {
  isFeaturedBet?: boolean;
  authorName?: string;
  sportName?: string;
}

const FuturesProps = ({
  odds,
  event,
  isFeaturedBet = false,
  authorName,
  sportName,
}: FuturesPropsProps) => {
  const { logAmplitudeEvent } = useAmplitude();
  const ampBaseUrl = odds?.event?.eventUrl || SPORTSBOOK_URL;
  const viewMoreOddsUrl = ampBaseUrl;
  const { marketName, selections: tempSelections } = odds.props?.[0]
    ? odds.props[0]
    : {
        marketName: "",
        selections: [],
      };
  const selections = filterEmpty(tempSelections ?? []);
  const [url, setUrl] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const { author, sport, title } = useArticleInfo();
  const parsedAuthorName = authorName ?? author;
  const parsedSportName = sportName ?? sport;

  const ampProps = useMemo(
    () => ({
      "Event Name": event?.name ?? "",
      Competition: parsedSportName ?? "",
      "Article Title": title ?? "",
      Author: parsedAuthorName ?? "",
    }),
    [parsedAuthorName, event?.name, parsedSportName, title]
  );

  const onOddsClick = (oddsUrl: string) => {
    setUrl(oddsUrl);
    logAmplitudeEvent(SPORTSBOOK_ODDS_SELECTED, {
      ...ampProps,
      Module: isFeaturedBet ? "featured_bet" : "coupon",
    });
    setShowModal(true);
  };

  const onViewMoreOddsClick = () => {
    logAmplitudeEvent(SPORTSBOOK_ODDS_CTA_CLICKED, {
      ...ampProps,
      "Link Text": "view_more_odds_in_sportsbook",
      "Link URL": ampBaseUrl,
      Module: isFeaturedBet ? "featured_bet" : "coupon",
    });
  };

  const onClickPrimaryCta = () => {
    logAmplitudeEvent(SPORTSBOOK_BETSLIP_CTA_CLICKED, {
      ...ampProps,
      "Link Text": "go_to_betslip",
      "Link URL": url,
      Module: "ready_to_bet_modal",
    });
  };

  const onRequestClose = () => {
    logAmplitudeEvent(SPORTSBOOK_READY_TO_BET_MODAL_CLOSED, {
      ...ampProps,
      Module: "ready_to_bet_modal",
    });
    setShowModal(false);
  };
  const mid = Math.round(selections?.length / 2);

  const col1 = selections?.slice(0, mid);
  const col2 = selections?.slice(mid);

  return (
    <>
      <Card className={Styles.card}>
        <CardHeader className={Styles.cardHeader}>{marketName}</CardHeader>
        <div className={Styles.cardContent}>
          <div className={Styles.column}>
            {col1?.map(
              ({
                selectionId,
                odds: oddsValue,
                runnerName,
                url: oddsUrl,
                handicap,
              }) => (
                <Selection
                  key={selectionId}
                  oddsValue={oddsValue}
                  runnerName={runnerName}
                  handicap={handicap}
                  onOddsClick={() => onOddsClick(oddsUrl ?? "")}
                />
              )
            )}
          </div>
          {col2?.length > 0 ? (
            <div className={Styles.column}>
              {col2?.map(
                ({
                  selectionId,
                  odds: oddsValue,
                  runnerName,
                  url: oddsUrl,
                  handicap,
                }) => (
                  <Selection
                    key={selectionId}
                    handicap={handicap}
                    oddsValue={oddsValue}
                    runnerName={runnerName}
                    onOddsClick={() => onOddsClick(oddsUrl ?? "")}
                  />
                )
              )}
            </div>
          ) : null}
        </div>
        <Divider color="light" />
        <div className={Styles.viewMoreOdds}>
          <NavigationLink
            href={viewMoreOddsUrl}
            linkText="View more odds in Sportsbook"
            onClick={onViewMoreOddsClick}
          />
        </div>
        <CardFooter className={Styles.footer}>
          Odds/lines subject to change
        </CardFooter>
      </Card>
      <RedirectToSportsbook
        onClickPrimaryCta={onClickPrimaryCta}
        onRequestClose={onRequestClose}
        isOpen={showModal}
        sportsbookRedirectUrl={url}
      />
    </>
  );
};

export default FuturesProps;
